import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  notifications as notifyApi,
  type NotificationPreference,
  type NotificationTemplate,
} from "@/lib/api";
import { strings } from "@/lib/strings";

/**
 * Notification preferences (NT-16 … NT-19).
 *
 * Opt-in throughout: nothing is sent until a volunteer asks for it. Each
 * message's exact wording is shown before they subscribe, so they can satisfy
 * themselves that nothing about a patient travels over SMS.
 */
export function NotificationSettings() {
  const { user } = useAuth();
  const { notify } = useToast();

  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([notifyApi.templates(), notifyApi.preferences()]);
      setTemplates(t);
      setPrefs(p);
    } catch {
      notify(strings.errors.generic, "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const prefFor = (code: string) => prefs.find((p) => p.template_code === code);

  const subscribe = async (template: NotificationTemplate, channel: string) => {
    try {
      await notifyApi.setPreference({ template_code: template.code, channel });
      notify(`You will be told about: ${template.description}`, "success");
      await load();
    } catch {
      notify(strings.errors.generic, "error");
    }
  };

  const unsubscribe = async (code: string) => {
    try {
      await notifyApi.removePreference(code);
      notify("Turned off. Nothing further will be sent.", "success");
      await load();
    } catch {
      notify(strings.errors.generic, "error");
    }
  };

  const saveQuietHours = async (
    pref: NotificationPreference,
    from: number | null,
    to: number | null,
  ) => {
    await notifyApi.setPreference({
      template_code: pref.template_code,
      channel: pref.channel,
      is_enabled: pref.is_enabled,
      quiet_from_hour: from,
      quiet_to_hour: to,
    });
    await load();
  };

  const savePhone = async () => {
    setBusy(true);
    try {
      await notifyApi.setPhone(phone, consent);
      notify("Number saved.", "success");
      setPhone("");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : strings.errors.generic,
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const removePhone = async () => {
    try {
      await notifyApi.clearPhone();
      notify("Number and consent both removed.", "success");
    } catch {
      notify(strings.errors.generic, "error");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Notifications</h1>
        <p className="text-sm text-slate-500">
          Nothing is sent unless you ask for it here.
        </p>
      </div>

      <Card>
        <CardHeader
          title="What a message can contain"
          description="A reminder never includes anything about a patient."
        />
        <CardBody>
          <p className="text-sm text-slate-700">
            Messages carry counts only — how many isolates need review, how many
            you entered. They never contain a hospital number, an organism, or a
            result. That is enforced in the platform's code, not left to whoever
            writes the next reminder.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Your mobile number"
          description="Only needed for SMS or WhatsApp. Email reminders use your hospital address."
        />
        <CardBody className="space-y-3">
          {user?.phone_e164 ? (
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone="ok">A number is on file</Badge>
              <Button variant="secondary" size="sm" onClick={() => void removePhone()}>
                Remove it
              </Button>
            </div>
          ) : (
            <>
              <Field
                id="phone"
                label="Mobile number"
                hint="0803 123 4567, or +234 803 123 4567."
              >
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  I agree to the platform storing my number so it can send me the
                  reminders I choose below. I can remove it at any time.
                </span>
              </label>
              <Button
                loading={busy}
                disabled={!phone.trim() || !consent}
                onClick={() => void savePhone()}
              >
                Save my number
              </Button>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="What to send me" />
        <CardBody className="space-y-4">
          {templates.map((template) => {
            const pref = prefFor(template.code);
            return (
              <div
                key={template.code}
                className="rounded-md border border-slate-200 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {template.description}
                    </p>
                    <p className="mt-1 rounded bg-slate-50 p-2 text-xs italic text-slate-600">
                      {template.example}
                    </p>
                  </div>
                  {pref ? (
                    <Badge tone="ok">On, by {pref.channel}</Badge>
                  ) : (
                    <Badge>Off</Badge>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                  {pref ? (
                    <>
                      <Field id={`${template.code}-quiet-from`} label="Quiet from">
                        <Select
                          id={`${template.code}-quiet-from`}
                          placeholder="No quiet hours"
                          value={pref.quiet_from_hour?.toString() ?? ""}
                          onChange={(e) =>
                            void saveQuietHours(
                              pref,
                              e.target.value === "" ? null : Number(e.target.value),
                              pref.quiet_to_hour,
                            )
                          }
                          options={Array.from({ length: 24 }, (_, h) => ({
                            value: String(h),
                            label: `${String(h).padStart(2, "0")}:00`,
                          }))}
                        />
                      </Field>
                      <Field id={`${template.code}-quiet-to`} label="until">
                        <Select
                          id={`${template.code}-quiet-to`}
                          placeholder="No quiet hours"
                          value={pref.quiet_to_hour?.toString() ?? ""}
                          onChange={(e) =>
                            void saveQuietHours(
                              pref,
                              pref.quiet_from_hour,
                              e.target.value === "" ? null : Number(e.target.value),
                            )
                          }
                          options={Array.from({ length: 24 }, (_, h) => ({
                            value: String(h),
                            label: `${String(h).padStart(2, "0")}:00`,
                          }))}
                        />
                      </Field>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void unsubscribe(template.code)}
                      >
                        Turn off
                      </Button>
                    </>
                  ) : (
                    template.channels.map((channel) => (
                      <Button
                        key={channel}
                        variant="secondary"
                        size="sm"
                        onClick={() => void subscribe(template, channel)}
                      >
                        Send by {channel}
                      </Button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </CardBody>
      </Card>
    </div>
  );
}
